import { computed, shallowRef, watch } from "vue";
import {
  isJust,
  isNothing,
  fromMaybe,
  just,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import { trimmedMaybe } from "../lib/trimmedString";
import { defineStore } from "pinia";
import { listen, type UnlistenFn } from "@tauri-apps/api/event";
import { getCloudSession } from "../lib/cloudAuth";
import { listChronicleEntries } from "../lib/chronicle";
import { getInstalledDesktopLicense } from "../lib/desktopLicense";
import { isTauri, joinProjectPath } from "../lib/runtime";
import { createTimeoutHandle } from "../lib/timeoutHandle";
import { readProjectFile } from "../lib/tauri/core";
import { countManuscriptWords } from "../lib/wordCount";
import { assertNever } from "../lib/assertNever";
import { dispatchInto } from "../lib/tea";
import {
  formatPrimaryGoalChip,
  goalTarget,
  goalsTooltipLines,
  localDateString,
  normalizeGoalsAuthor,
  primaryGoalKind,
  progressForKind,
  toGoalsChronicleEntry,
  type GoalKind,
} from "../lib/writingGoals";
import { normalizeZwGoals, sceneFilesInOrder } from "../lib/zwConfig";
import { useEditorSessionStore } from "./useEditorSessionStore";
import {
  initialGoalsModel,
  updateGoals,
  type ActiveBuffer,
  type GoalsCmd,
  type GoalsMsg,
} from "./useProjectGoalsStore.update";

export type PrimaryGoalChip = {
  label: string;
  title: string;
  met: boolean;
};

export type GoalsProgress = {
  session: number;
  daily: number;
  project: number;
};

/**
 * Writing-goal progress (session / today / book).
 *
 * TEA: pure Model/Msg/Cmd in `useProjectGoalsStore.update.ts`. This shell
 * gathers facts (author, chronicle, active buffer), dispatches them as Msgs,
 * and interprets Cmds (commit listener, chronicle refresh, debounce timer).
 */
export const useProjectGoalsStore = defineStore("projectGoals", () => {
  const session = useEditorSessionStore();

  const model = shallowRef(initialGoalsModel);

  let goalsProgressToken = 0;
  const chronicleRefreshTimer = createTimeoutHandle();
  let unlistenCommitted: Maybe<UnlistenFn> = nothing;

  async function runCmd(cmd: GoalsCmd): Promise<void> {
    switch (cmd.kind) {
      case "ensureCommitListener":
        // Fire and forget: progress must not wait on the event bridge.
        void ensureCommitListener();
        return;
      case "refreshChronicle":
        await refreshChronicleProgress();
        return;
      case "clearRefreshTimer":
        chronicleRefreshTimer.clear();
        return;
      default:
        assertNever(cmd);
    }
  }

  function send(msg: GoalsMsg): Promise<void> {
    return dispatchInto(model, msg, updateGoals, runCmd);
  }

  // Field projections keep reactivity as fine-grained as the old per-field
  // refs: a computed only notifies when its own slice of the Model changes
  // (e.g. a new `goalsAuthorEmail` must not re-run `goalsProgress`).
  const fileWordCounts = computed(() => model.value.fileWordCounts);
  const alignedWordCounts = computed(() => model.value.alignedWordCounts);
  const chronicleSessionWords = computed(
    () => model.value.chronicleSessionWords,
  );
  const chronicleDailyWords = computed(() => model.value.chronicleDailyWords);
  const chronicleForDate = computed(() => model.value.chronicleForDate);

  const goals = computed(() =>
    normalizeZwGoals(fromMaybe(session.projectConfig)?.goals),
  );

  const manuscriptTotal = computed(() => {
    const files = sceneFilesInOrder(session.manuscript);
    const activeChapter = session.outlineActiveFile;
    let total = 0;
    for (const name of files) {
      if (isJust(activeChapter) && name === activeChapter.value) {
        total += countManuscriptWords(session.doc);
      } else {
        total += fileWordCounts.value[name] ?? 0;
      }
    }
    return total;
  });

  /**
   * Words in the active buffer not yet folded into chronicle Session/Today
   * (typing + autosaved-but-uncommitted).
   */
  const bufferDelta = computed(() => {
    if (session.activeMap) return 0;
    const path = session.activeBasename;
    if (isNothing(path)) return 0;
    const aligned = toMaybe(alignedWordCounts.value[path.value]);
    if (isNothing(aligned)) return 0;
    return countManuscriptWords(session.doc) - aligned.value;
  });

  const goalsProgress = computed((): GoalsProgress => {
    const today = localDateString();
    const forDate = chronicleForDate.value;
    if (isJust(forDate) && forDate.value !== today) {
      void ensureGoalProgress();
    }
    const delta = bufferDelta.value;
    return {
      session: Math.max(0, chronicleSessionWords.value + delta),
      daily: Math.max(0, chronicleDailyWords.value + delta),
      project: manuscriptTotal.value,
    };
  });

  const primaryGoalChip = computed((): Maybe<PrimaryGoalChip> => {
    if (session.hideGoalsChip) return nothing;
    const g = goals.value;
    const wordsForKind = (kind: GoalKind): Maybe<number> => {
      if (kind === "session") return toMaybe(g.sessionWords);
      if (kind === "daily") return toMaybe(g.dailyWords);
      return toMaybe(g.projectWords);
    };
    return matchMaybe(primaryGoalKind(just(g)), {
      nothing: (): Maybe<PrimaryGoalChip> => nothing,
      just: (kindId): Maybe<PrimaryGoalChip> =>
        matchMaybe(goalTarget(wordsForKind(kindId)), {
          nothing: (): Maybe<PrimaryGoalChip> => nothing,
          just: (target): Maybe<PrimaryGoalChip> => {
            const progress = goalsProgress.value;
            const current = progressForKind(kindId, progress);
            return just({
              label: formatPrimaryGoalChip(kindId, current, target),
              title: goalsTooltipLines(g, progress),
              met: current >= target,
            });
          },
        }),
    });
  });

  async function resolveGoalsAuthorEmail(): Promise<Maybe<string>> {
    const cloud = normalizeGoalsAuthor(trimmedMaybe(getCloudSession()?.email));
    if (isJust(cloud)) return cloud;
    try {
      const license = await getInstalledDesktopLicense();
      const fromLicense = normalizeGoalsAuthor(trimmedMaybe(license?.email));
      return fromLicense;
    } catch {
      return nothing;
    }
  }

  async function ensureCommitListener(): Promise<void> {
    if (isJust(unlistenCommitted) || isTauri() === false) return;
    try {
      const unlisten = await listen<{ root?: string }>(
        "project-committed",
        (event) => {
          const root = trimmedMaybe(event.payload?.root);
          if (
            isNothing(root) ||
            isNothing(session.rootPath) ||
            root.value !== session.rootPath.value
          ) {
            return;
          }
          void refreshChronicleProgress();
        },
      );
      unlistenCommitted = just(unlisten);
    } catch {
      // Non-Tauri / event bridge unavailable.
    }
  }

  /** The open prose buffer, if any (maps have no word-count alignment). */
  function activeBuffer(): Maybe<ActiveBuffer> {
    const path = session.activeBasename;
    if (isNothing(path) || session.activeMap !== false) return nothing;
    return just({
      pathKey: path.value,
      docWords: countManuscriptWords(session.doc),
      dirty: session.dirty,
    });
  }

  async function refreshManuscriptWordCounts(): Promise<void> {
    if (isNothing(session.rootPath)) {
      return;
    }
    const files = sceneFilesInOrder(session.manuscript);
    if (files.length === 0) {
      void send({ type: "WordCountsLoaded", files, counts: {} });
      return;
    }
    const root = session.rootPath.value;
    const activeChapter = session.outlineActiveFile;
    const counts: Record<string, number> = {};
    await Promise.all(
      files.map(async (name) => {
        if (isJust(activeChapter) && name === activeChapter.value) {
          counts[name] = countManuscriptWords(session.doc);
          return;
        }
        try {
          const content = await readProjectFile(joinProjectPath(root, name));
          counts[name] = countManuscriptWords(content);
        } catch {
          counts[name] = model.value.fileWordCounts[name] ?? 0;
        }
      }),
    );
    void send({ type: "WordCountsLoaded", files, counts });
  }

  async function refreshChronicleProgress(): Promise<void> {
    if (isNothing(session.rootPath)) return;
    const root = session.rootPath.value;
    const token = ++goalsProgressToken;
    const author = await resolveGoalsAuthorEmail();
    if (token !== goalsProgressToken) return;
    if (fromMaybe(session.rootPath) !== root) return;
    void send({
      type: "AuthorResolved",
      author,
      nowUnix: Math.floor(Date.now() / 1000),
    });

    const today = localDateString();
    try {
      const entries = await listChronicleEntries(root);
      if (token !== goalsProgressToken) return;
      if (fromMaybe(session.rootPath) !== root) return;
      void send({
        type: "ChronicleLoaded",
        author,
        entries: entries.map((entry) =>
          toGoalsChronicleEntry({
            author: entry.author ?? null,
            timestamp: entry.timestamp,
            wordsDiff: entry.wordsDiff ?? null,
          }),
        ),
        maxSeq: entries.reduce(
          (max, entry) => Math.max(max, entry.seq ?? 0),
          0,
        ),
        today,
        active: activeBuffer(),
      });
    } catch (error) {
      console.warn("[goals] failed to read chronicle for progress", error);
    }
  }

  /**
   * Ensure session clock + author-filtered chronicle totals are ready.
   * Session/Today come from chronicle (all prose paths, current user only)
   * plus the live buffer delta.
   */
  async function ensureGoalProgress(
    opts: { resetSession?: boolean } = {},
  ): Promise<void> {
    if (isNothing(session.rootPath)) return;
    await send({
      type: "EnsureProgress",
      resetSession: opts.resetSession === true,
      nowUnix: Math.floor(Date.now() / 1000),
      active: activeBuffer(),
    });
  }

  /** Alias kept for existing call sites during the chronicle migration. */
  async function ensureGoalBaselines(
    opts: { resetSession?: boolean } = {},
  ): Promise<void> {
    await ensureGoalProgress(opts);
  }

  /** Cheap retry for light scene navigation when open-time resolve raced. */
  function ensureGoalProgressIfNeeded(): void {
    if (
      isJust(model.value.sessionStartedAtUnix) &&
      isJust(model.value.chronicleForDate)
    ) {
      return;
    }
    void ensureGoalProgress();
  }

  function scheduleChronicleRefresh(delayMs = 400): void {
    chronicleRefreshTimer.schedule(delayMs, () => {
      void refreshChronicleProgress();
    });
  }

  watch(
    () => sceneFilesInOrder(session.manuscript).join("\u0000"),
    () => {
      if (
        isJust(session.rootPath) &&
        isJust(model.value.sessionStartedAtUnix)
      ) {
        void refreshManuscriptWordCounts();
      }
    },
  );

  /**
   * Align goals to the loaded buffer (scene switch / open). Resets the
   * not-yet-chronicled delta to 0 for this path.
   */
  function noteActiveChapterSaved(relativePath: string, content: string): void {
    void send({
      type: "NoteActiveChapterSaved",
      relativePath,
      words: countManuscriptWords(content),
      sceneFiles: sceneFilesInOrder(session.manuscript),
    });
  }

  /**
   * Record a successful disk flush without advancing chronicle alignment.
   * Keeps Session/Today counting those words via bufferDelta until the next
   * chronicle refresh after commit.
   */
  function noteFileFlushed(relativePath: string, content: string): void {
    void send({
      type: "NoteFileFlushed",
      relativePath,
      words: countManuscriptWords(content),
      sceneFiles: sceneFilesInOrder(session.manuscript),
    });
  }

  function resetWordCounts(): void {
    void send({ type: "ResetWordCounts" });
  }

  function resetSessionBaseline(): void {
    void send({ type: "ResetSessionBaseline" });
  }

  return {
    manuscriptTotal,
    goalsProgress,
    primaryGoalChip,
    fileWordCounts,
    refreshManuscriptWordCounts,
    ensureGoalProgress,
    ensureGoalBaselines,
    ensureGoalProgressIfNeeded,
    refreshChronicleProgress,
    scheduleChronicleRefresh,
    noteActiveChapterSaved,
    noteFileFlushed,
    resetWordCounts,
    resetSessionBaseline,
  };
});
